export const SIZE_ORDER = [
  'XXS',
  'XS',
  'XS/S',
  'S',
  'S/M',
  'M',
  'M/L',
  'L',
  'L/XL',
  'XL',
  'XXL',
  'XXXL',
  '3XL',
  '4XL',
  '5XL'
];

const SIZE_ORDER_INDEX = new Map(
  SIZE_ORDER.map((size, index) => [normalizeSize(size), index])
);

function normalizeSize(size: string): string {
  return String(size || '').trim().toUpperCase().replace(/\s+/g, '');
}

function numericParts(size: string): number[] | null {
  const normalized = normalizeSize(size).replace(',', '.');
  if (!/^\d+(\.\d+)?([/-]\d+(\.\d+)?)*$/.test(normalized)) {
    return null;
  }

  return normalized.split(/[/-]/).map(Number);
}

function compareNumberParts(a: number[], b: number[]): number {
  const length = Math.max(a.length, b.length);

  for (let i = 0; i < length; i += 1) {
    const aValue = a[i] ?? Number.NEGATIVE_INFINITY;
    const bValue = b[i] ?? Number.NEGATIVE_INFINITY;

    if (aValue !== bValue) {
      return aValue - bValue;
    }
  }

  return 0;
}

function alphaRank(size: string): number | null {
  const normalized = normalizeSize(size);
  const exactRank = SIZE_ORDER_INDEX.get(normalized);

  if (exactRank !== undefined) {
    return exactRank;
  }

  const firstPart = normalized.split(/[/-]/)[0];
  const firstPartRank = SIZE_ORDER_INDEX.get(firstPart);

  return firstPartRank === undefined ? null : firstPartRank + 0.5;
}

export function compareSizes(a: string, b: string): number {
  const aNumeric = numericParts(a);
  const bNumeric = numericParts(b);

  if (aNumeric && bNumeric) {
    return compareNumberParts(aNumeric, bNumeric);
  }

  if (aNumeric) return -1;
  if (bNumeric) return 1;

  const aAlphaRank = alphaRank(a);
  const bAlphaRank = alphaRank(b);

  if (aAlphaRank !== null && bAlphaRank !== null) {
    return aAlphaRank - bAlphaRank;
  }

  if (aAlphaRank !== null) return -1;
  if (bAlphaRank !== null) return 1;

  return normalizeSize(a).localeCompare(normalizeSize(b), 'tr');
}

export function sortSizeKeys(sizes: string[]): string[] {
  return [...sizes].sort(compareSizes);
}

export function sortSizeEntries<T>(sizes: Record<string, T> | null | undefined): Array<[string, T]> {
  return Object.entries(sizes || {}).sort(([a], [b]) => compareSizes(a, b));
}
